import { useEffect } from 'react'
import { create } from 'zustand'
import { AlertTriangle } from 'lucide-react'
import { Modal } from './Modal'
import { Button } from './Button'

export interface ConfirmOptions {
  title?: string
  confirmLabel?: string
  cancelLabel?: string
  /** `danger` = nevratná akce (červené tlačítko + ikona varování). */
  tone?: 'danger' | 'primary'
}

interface ConfirmState {
  open: boolean
  message: string
  options: ConfirmOptions
  resolve: ((ok: boolean) => void) | null
  ask: (message: string, options?: ConfirmOptions) => Promise<boolean>
  close: (ok: boolean) => void
}

const useConfirmStore = create<ConfirmState>((set, get) => ({
  open: false,
  message: '',
  options: {},
  resolve: null,
  ask: (message, options = {}) =>
    new Promise<boolean>((resolve) => {
      // Případný předchozí dotaz bez odpovědi se bere jako „ne".
      get().resolve?.(false)
      set({ open: true, message, options, resolve })
    }),
  close: (ok) => {
    get().resolve?.(ok)
    set({ open: false, resolve: null })
  },
}))

/**
 * Imperativní potvrzení — náhrada `window.confirm`:
 * `if (await confirm('Opravdu zrušit nepřítomnost?', { tone: 'danger', confirmLabel: 'Zrušit nepřítomnost' })) { … }`
 * Enter potvrdí, Escape zruší. Vyžaduje jednou vykreslený `<ConfirmHost />` (je v App.tsx).
 */
export function confirm(message: string, options?: ConfirmOptions | string): Promise<boolean> {
  const opts = typeof options === 'string' ? { title: options } : options
  return useConfirmStore.getState().ask(message, opts)
}

export function ConfirmHost() {
  const { open, message, options, close } = useConfirmStore()
  const tone = options.tone ?? 'danger'

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') { e.preventDefault(); close(true) }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, close])

  return (
    <Modal
      open={open}
      onClose={() => close(false)}
      title={options.title ?? 'Potvrzení'}
      icon={tone === 'danger' ? <AlertTriangle size={20} /> : undefined}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => close(false)}>{options.cancelLabel ?? 'Zpět'}</Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={() => close(true)}>{options.confirmLabel ?? 'Potvrdit'}</Button>
        </>
      }
    >
      <p className="whitespace-pre-line text-sm text-ink-700">{message}</p>
    </Modal>
  )
}
